import dayjs from 'dayjs';

import type { CashAccountBalance, CashCount, CashLedgerRow, CashPosition } from '../api/cash';
import { paymentMethodLabel } from '../bookings/paymentWords';
import { formatAmount } from '../format/money';
import { missesOpeningBalance, sourceLabels } from './position';

/**
 * The phone's Cash and bank screen in words (`docs/Mobile_Redesign_Plan.md`, step 12): each
 * account's line, each of today's movements, and the More tab's one line about the drawer.
 *
 * <p>
 * <strong>The headline is never named here</strong> — that is `position.ts`'s `headlineLabel`, so
 * the phone cannot claim "Cash in hand" where the website says "Money in, less money out".
 * </p>
 */

/** The day an account was last counted, or null if it never has been. */
export function lastCountedOn(
  counts: readonly CashCount[] | undefined,
  accountId: number,
): string | null {
  let last: string | null = null;

  for (const count of counts ?? []) {
    const day = count.countedOn.slice(0, 10);

    if (count.cashAccountId === accountId && (last === null || day > last)) {
      last = day;
    }
  }

  return last;
}

export type LineTone = 'muted' | 'warning';

/**
 * When a drawer was last counted. Amber unless it was today — a drawer left uncounted is the thing
 * a shop owner wants to notice, and "yesterday" is already one evening missed.
 */
export function countedWords(
  lastCounted: string | null,
  today: string,
): { text: string; tone: LineTone } {
  if (lastCounted === null) {
    return { text: 'Never counted', tone: 'warning' };
  }

  if (lastCounted === today) {
    return { text: 'Counted today', tone: 'muted' };
  }

  const text =
    lastCounted === dayjs(today).subtract(1, 'day').format('YYYY-MM-DD')
      ? 'Last counted yesterday'
      : `Last counted ${dayjs(lastCounted).format('D MMM')}`;

  return { text, tone: 'warning' };
}

/**
 * The line under an account's name.
 *
 * <p>
 * Most-pressing first: a missing float is what stops the headline being a balance at all, so it
 * outranks when the drawer was counted; a retired account says so, since it takes nothing new; a
 * bank account says what lands in it, because a shop reads "HDFC" and wonders why its UPI takings
 * are there.
 * </p>
 */
export function accountLine(
  account: CashAccountBalance,
  counts: readonly CashCount[] | undefined,
  today: string,
): { text: string; tone: LineTone } {
  if (missesOpeningBalance(account)) {
    return { text: 'No opening balance yet — set it on the website', tone: 'warning' };
  }

  if (!account.isActive) {
    return { text: 'Retired · takes nothing new', tone: 'muted' };
  }

  if (account.kind === 'Bank') {
    return {
      text: account.isDefault ? 'UPI, card and bank payments land here' : 'Bank account',
      tone: 'muted',
    };
  }

  return countedWords(lastCountedOn(counts, account.id), today);
}

/** One of today's movements: what it was, then its paper and how it moved. */
export function ledgerRowWords(row: CashLedgerRow): { title: string; meta: string } {
  const label = sourceLabels[row.sourceKind];
  const note = row.notes?.trim();

  return {
    title: note ? `${label} · ${note}` : label,
    meta: [row.sourceDocumentNumber || null, paymentMethodLabel[row.instrument].toLowerCase()]
      .filter(Boolean)
      .join(' · '),
  };
}

/** The account a branch's cash goes to: its default till, else the first active one counted. */
export function drawerOf(position: CashPosition | undefined): CashAccountBalance | null {
  const accounts = position?.accounts ?? [];

  return (
    accounts.find((account) => account.kind === 'Till' && account.isDefault) ??
    accounts.find((account) => account.kind !== 'Bank' && account.isActive) ??
    null
  );
}

/**
 * The More tab's line under Cash and bank: "Drawer ₹42,300 · not counted today". Null while the
 * position is loading, and plain words for a branch no money has reached yet.
 */
export function cashRowLine(
  position: CashPosition | undefined,
  counts: readonly CashCount[] | undefined,
  today: string,
  currency = '',
): string | null {
  if (!position) {
    return null;
  }

  const drawer = drawerOf(position);

  if (!drawer) {
    return 'Nothing has moved through the drawer yet';
  }

  const counted =
    lastCountedOn(counts, drawer.id) === today ? 'counted today' : 'not counted today';

  // Without a float the figure is movement, not what is in the drawer — `position.ts`'s rule, so
  // the line names no amount rather than calling movement the drawer.
  if (missesOpeningBalance(drawer)) {
    return `No opening balance yet · ${counted}`;
  }

  return `Drawer ${currency}${formatAmount(drawer.closing)} · ${counted}`;
}
